/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { ProgressIndicator, ProgressStep } from 'carbon-components-react';
import PropTypes from 'prop-types';
import React, { useEffect, useCallback } from 'react';
import { useIntl } from 'react-intl';

import { useQuickSetupContext } from '../../../hooks/useQuickSetupContext';
import { TabContent } from '../tabbed-header/TabContent';
import { messages } from './messages';

import './QuickSetup.scss';

const QuickSetup: React.FC<any> = props => {
  const {
    currentStep,
    setCurrentStep,
    steps,
    setSteps,
    filteredSteps,
    setFilteredSteps,
    shouldDisableStep
  } = useQuickSetupContext();

  // Need to wrap the Image so it will be in color and work with TabContent
  const intl = useIntl();

  // go through steps and determine if current step should be disabled
  // because if any previous steps are REQUIRED but NOT COMPLETE, we should
  // be disabled.

  useEffect(() => {
    // We only do this if the Number of steps change, no reason to otherwise.
    if (React.Children.count(props.children) !== steps.length) {
      const childSteps = React.Children.toArray(props.children).map(
        (child: any, index: number) => {
          return {
            key: child.key,
            index: index,
            stepType: child.props?.stepType,
            label: child.props?.label,
            secondaryLabel: child.props?.secondaryLabel,
            readyToContinue: false,
            required: child.props?.required,
            title: child.props?.title,
            child: child
          };
        }
      );
      setSteps(childSteps);
      setFilteredSteps(childSteps);
    }
  }, [intl, props.children, setFilteredSteps, setSteps, steps]);

  const renderSteps = useCallback(() => {
    return filteredSteps?.map((step, index) => {
      return (
        <ProgressStep
          title={
            step.title ??
            step.label ??
            intl.formatMessage(messages.step, { index: index })
          }
          key={step.key}
          label={
            step.label ?? intl.formatMessage(messages.step, { index: index })
          }
          secondaryLabel={step.secondaryLabel}
          current={step.current}
          disabled={shouldDisableStep(index)}
        />
      );
    });
  }, [filteredSteps, intl, shouldDisableStep]);

  const renderStep = useCallback(
    step => {
      return filteredSteps ? filteredSteps[step]?.child : null;
    },
    [filteredSteps]
  );

  return (
    <>
      <TabContent title={props.title} ImageComponent={props.icon}>
        <div className="bx--col">
          <div className="TabContent__body-content TabContent__body-outdent">
            <ProgressIndicator
              className="QuickSetup QuickSetup__progress-indicator"
              currentIndex={currentStep}
              onChange={index => {
                setCurrentStep(index);
              }}>
              {renderSteps()}
            </ProgressIndicator>
          </div>
          {renderStep(currentStep)}
        </div>
      </TabContent>
    </>
  );
};

QuickSetup.propTypes = {
  /**
   * Title of the QuickSetup displayed at top
   */
  title: PropTypes.any,
  /**
   * Icon to show
   */
  icon: PropTypes.any,
  /**
   * Children
   */
  children: PropTypes.node
};

export { QuickSetup };
